import { Injectable, inject } from '@angular/core';

import {
    BasicObject,
    CartModel,
    Product,
    QUOTE_CONTEXTS,
    QuoteContextRenewalModel,
    QuoteModel,
} from '@bytel/bytel-sales';
import {
    CheckoutCartModel,
    CheckoutStorageService,
    DeliveryInfo,
    FUNDING_ENUM,
    IAddress,
    IApiBillingAddressParams,
    IApiShippingMethodParams,
    IShippingMethodData,
    SHIPPING_MODES,
    ShippingMethodModel,
    Store,
    UserInfos,
    UserService,
} from '@bytel/checkout';

import { CheckoutOauth2ResourcesService } from '@services/oauth2-ressources.service';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PriceTypeUtils } from '../helper/price-type.helper';
import { SalesApiHelper } from '../helper/sales-api.helper';
import { GetShippingMethodPayload } from '../interfaces/address.interface';
import { IShippingMethodResponse } from '../interfaces/shipping.interface';
import { ShippingProductInput, ShippingQuoteInput } from './../interfaces/address.interface';
import { MixedCartService } from './../services/mixed-cart.service';

@Injectable({
    providedIn: 'root',
})
export class AddressRepository {
    private _franceCountryValues: string[] = ['FRANCE', 'FR'];

    private currentCartModel: CartModel;

    private checkoutStorageService: CheckoutStorageService = inject(CheckoutStorageService);

    public constructor(
        private oauth2Resource: CheckoutOauth2ResourcesService,
        private userService: UserService,
        private mixedCartService: MixedCartService,
    ) {}

    /**
     * Get available shipping method for mixed cart
     * @param cartModel Current cart model
     * @returns Array of available shipping method
     */
    public getAvailableShippingMethods(
        cartModel: CheckoutCartModel,
        quoteIndex: number,
    ): Observable<ShippingMethodModel[]> {
        this.currentCartModel = cartModel;
        const payload: GetShippingMethodPayload = this._createGetShippingMethodPayload(cartModel, quoteIndex);
        return this.oauth2Resource
            .ventes()
            .panier()
            .livraisonsDispo()
            .addCache()
            .useSalesApi()
            .displayErrors()
            .post(payload)
            .pipe(
                map((result: IShippingMethodResponse) => {
                    const shippings = result.panier.parcours.map((quote) =>
                        quote.modesLivraison.map((ml) => {
                            const model = this._createShippingMethodModel(ml);
                            model.quoteType = quote.type as QUOTE_CONTEXTS;
                            return model;
                        }),
                    );
                    return shippings.reduce((prev, current) => [...prev, ...current], []);
                }),
            );
    }

    public postShippingMethods(
        delivery: DeliveryInfo,
        cartModel: CheckoutCartModel,
        quote: QuoteModel,
        shippingAddress: IAddress,
    ): Observable<ShippingMethodModel> {
        let { nom, prenom } = this.userService.user;
        const storedLegalRepresentative: UserInfos = this.userService.user?.representantLegal;
        const civilite = this.userService.user.getCivility();

        if (storedLegalRepresentative) {
            nom = storedLegalRepresentative.nom;
            prenom = storedLegalRepresentative.prenom;
        }

        if (!nom || !prenom) {
            throw new Error('lastName or firstName is missing');
        }

        let shippingAddressToPost: IAddress = delivery?.pickup
            ? this._mapAddressFromPickup(delivery.pickup)
            : shippingAddress;
        if (this.mixedCartService.isMixedCart(cartModel) && !quote.isAcquisitionFix()) {
            shippingAddressToPost = shippingAddress;
        }

        const params: IApiShippingMethodParams = {
            adresse: {
                ...this._filterAddressFieldForApi(shippingAddressToPost),
                civilite,
                nom,
                prenom,
            },
            // For the time being when pickup controleAutoGCP must be false
            controleAutoGCP: delivery?.pickup ? false : !!shippingAddress?.isGcpValidated,
            modeLivraison: this._handleDelivery(cartModel, quote, delivery),
        };

        return this.oauth2Resource
            .ventes()
            .panier(cartModel.cartId)
            .parcours(quote.id)
            .adresseLivraison()
            .setLocalService()
            .useSalesApi()
            .post(params)
            .pipe(map((result) => new ShippingMethodModel(result.methodeEnvoi)));
    }

    public postBillingAddress(
        address: IAddress,
        cartModel: CheckoutCartModel,
        quoteId: number,
    ): Observable<{ message: string }> {
        const params: IApiBillingAddressParams = {
            ...this._filterAddressFieldForApi(address),
            controleAutoGCP: !!address.isGcpValidated,
        };
        return this.oauth2Resource
            .ventes()
            .panier(cartModel.cartId)
            .parcours(quoteId)
            .adresseFacturation()
            .setLocalService()
            .useSalesApi()
            .displayErrors()
            .post(params);
    }

    /**
     * Handle delivery for cart
     * If mixedcart & sowo or simo into quote return "LA_POSTE"
     * @param quote Current quote
     * @param delivery Current delivery infos
     * @returns BasicObject
     */
    private _handleDelivery(cartModel: CartModel, quote: QuoteModel, delivery: DeliveryInfo): BasicObject {
        if (this.mixedCartService.isMixedCart(cartModel) && !quote.isAcquisitionFix()) {
            return {
                gencode: SHIPPING_MODES.LA_POSTE,
            };
        } else {
            return delivery.getApiData();
        }
    }

    private _mapAddressFromPickup(pickup: Store): IAddress {
        const addressData: IAddress = {
            codePostal: pickup.postalCode.toString(),
            ville: pickup.city,
            rue: pickup.street,
            pays: pickup.country,
            complementAdresse: pickup.additionnalData || '',
            coordonnees: pickup.coordinates,
            numeroRue: pickup.street_number?.toString(),
        };

        return this._cleanAddress(addressData);
    }

    private _filterAddressFieldForApi(address: IAddress): IAddress {
        const country: string =
            address.pays && this._franceCountryValues.includes(address.pays.toUpperCase()) ? 'FRA' : address.pays;

        address.pays = country;
        const addressData: IAddress = {
            codePostal: address.codePostal,
            ville: address.ville,
            rue: address.rue,
            pays: country,
            complementAdresse: address.complementAdresse,
            residence: address.residence,
            batiment: address.batiment,
            escalier: address.escalier,
            etage: address.etage,
            numeroRue: address.numeroRue,
        };

        return this._cleanAddress(addressData);
    }

    private _createShippingMethodModel(shippingMethodData: IShippingMethodData): ShippingMethodModel {
        return new ShippingMethodModel({
            gencode: shippingMethodData.gencode,
            label: shippingMethodData.libelle,
            price: shippingMethodData.prix,
            estimatedDeveliveryDate: shippingMethodData.dateLivraisonEstimee,
            estimatedLatestDeliveryDate: shippingMethodData.dateLivraisonEstimeeAuPlusTard,
            limitHour: shippingMethodData.heureLimite,
            priceType: PriceTypeUtils.priceTypeSimToEnum(shippingMethodData.typePrix),
        });
    }

    private _createGetShippingMethodPayload(cartModel: CartModel, quoteIndex: number): GetShippingMethodPayload {
        try {
            return {
                noPersonne: (cartModel as CheckoutCartModel)?.userId,
                contractId:
                    (cartModel.getQuote(quoteIndex).context as QuoteContextRenewalModel)?.contractId?.toString() ??
                    null,
                typeClient: cartModel.getCustomerCategory(),
                adresse: this._cleanAddress(cartModel.getQuote(quoteIndex).address?.shipping),
                panier: {
                    parcours: cartModel.quotes.map((quote) => this._createGetShippingQuote(quote)),
                },
            };
        } catch (error) {
            console.log(error);
            throw error;
        }
    }

    private _createGetShippingQuote(quote: QuoteModel): ShippingQuoteInput {
        return {
            type: quote.context?.id,
            idVirtuel: 0,
            produits: quote.products
                .filter((p) => SalesApiHelper.IsApiProduct(p))
                .map((product) => this._createGetShippingProduct(product)),
        };
    }

    private _createGetShippingProduct(product: Product): ShippingProductInput {
        return {
            gencode: product.gencode,
            idVirtuel: 0,
            modeDeFinancement: this._getFundingMode(),
        };
    }

    private _getCreditTypeByLoan(nbLoan: number, type: string): FUNDING_ENUM {
        switch (type === FUNDING_ENUM.COFIDIS ? nbLoan + 1 : nbLoan) {
            case 3:
                return type === FUNDING_ENUM.YOUNITED ? FUNDING_ENUM.YOUNITED_X3 : FUNDING_ENUM.COFIDIS_X3;
            case 12:
                return type === FUNDING_ENUM.YOUNITED ? FUNDING_ENUM.YOUNITED_X12 : FUNDING_ENUM.COFIDIS_X12;
            case 24:
                return type === FUNDING_ENUM.YOUNITED ? FUNDING_ENUM.YOUNITED_X24 : FUNDING_ENUM.COFIDIS_X24;
            case 36:
                return FUNDING_ENUM.YOUNITED_X36;
            default:
                return type as FUNDING_ENUM;
        }
    }

    private _getFundingMode(): string {
        const currentMode = (this.currentCartModel as CheckoutCartModel)?.fundingMethod;
        if (!currentMode) {
            return FUNDING_ENUM.CASH;
        }
        if (currentMode.type === FUNDING_ENUM.YOUNITED || currentMode.type === FUNDING_ENUM.COFIDIS) {
            const selectedFunding = this.checkoutStorageService.get('funding_selected', true);
            return this._getCreditTypeByLoan(
                selectedFunding ? Number(selectedFunding) : currentMode.commercialProposal[0].nbLoan,
                currentMode.type,
            );
        }

        return currentMode.type;
    }

    private _cleanAddress(address: IAddress): IAddress {
        Object.keys(address).forEach((key) => {
            if (!address[key]) {
                delete address[key];
            }
        });

        return address;
    }
}
